import { Component } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { HTTPService } from 'src/app/main/services/HTTPService';

interface ImportResult {
  totalRows: number;
  successCount: number;
  errors: string[];
}

@Component({
  selector: 'app-import-book',
  templateUrl: './import-book.component.html',
  styleUrls: ['./import-book.component.css'],
})
export class ImportBookComponent {
  selectedFile: File | null = null;
  uploading = false;
  result: ImportResult | null = null;

  // Shown to the user so they know the expected column layout.
  // Mirrors BookImportService.EXPECTED_HEADERS on the backend.
  readonly templateHeaders = [
    'title', 'publisher', 'category', 'quantity', 'isbn', 'edition',
    'edition_year', 'writer', 'publishing_year', 'publication_place',
    'number_of_pages', 'notes',
  ];

  readonly requiredFields = ['title', 'category', 'quantity', 'writer'];

  constructor(
    public activeModal: NgbActiveModal,
    private toastr: ToastrService,
    private httpService: HTTPService
  ) {}

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length ? input.files[0] : null;

    if (!file) {
      this.selectedFile = null;
      return;
    }

    const validExtensions = ['.csv', '.xlsx', '.xls'];
    const isValid = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));

    if (!isValid) {
      this.toastr.error('Please select a .csv, .xlsx, or .xls file.', 'Invalid file type');
      this.selectedFile = null;
      input.value = '';
      return;
    }

    this.selectedFile = file;
    this.result = null;
  }

  upload(): void {
    if (!this.selectedFile) {
      this.toastr.error('Please choose a file first.', 'No file selected');
      return;
    }

    this.uploading = true;
    this.result = null;

    this.httpService.importBooks(this.selectedFile).subscribe(
      (res: ImportResult) => {
        this.uploading = false;
        this.result = res;

        if (res.successCount > 0) {
          this.toastr.success(
            `${res.successCount} of ${res.totalRows} book(s) imported successfully.`,
            'Import complete'
          );
        }
        if (res.errors && res.errors.length > 0) {
          this.toastr.warning(
            `${res.errors.length} row(s) could not be imported. See details below.`,
            'Some rows skipped'
          );
        }
      },
      (err) => {
        this.uploading = false;
        const message = err?.error || err?.message || 'Import failed. Please check the file and try again.';
        this.toastr.error(message, 'Import failed');
      }
    );
  }

  downloadTemplate(): void {
    const csvContent = this.templateHeaders.join(',') + '\n';
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'book_import_template.csv';
    link.click();
    window.URL.revokeObjectURL(url);
  }

  onCancelClick(): void {
    this.activeModal.dismiss();
  }

  onDoneClick(): void {
    // Pass back whether anything was actually imported so the caller
    // (book list) can decide whether to refresh its data.
    this.activeModal.close(this.result && this.result.successCount > 0);
  }
}